import React, { useEffect, useMemo, useState } from 'react';
import './DeskMap.css';
import './FunMode.css';
import { toggleFunMode, isFunModeActive, setFunMode, updateFunMode } from './FunMode';

const TABLE_LAYOUT = {
    width: 217.8,  // 198 * 1.1
    height: 314.6, // 286 * 1.1
    marginX: 81,  // Centered for 380px viewBox
    marginY: 73,  // Adjusted for 400px viewBox
};

const CELL_LAYOUT = {
    width: TABLE_LAYOUT.width / 2,
    height: TABLE_LAYOUT.height / 3,
};

const CHAIR_LAYOUT = {
    width: 38.72,  // 35.2 * 1.1
    height: 50.82, // 46.2 * 1.1
    offset: 29.04, // 26.4 * 1.1
};

// Unit color mapping based on floor plan
const UNIT_COLORS = {
    'Front Office Programme': { bg: '#ec4899', text: '#fff' },  // Pink
    'Front Office Representative': { bg: '#f472b6', text: '#fff' },  // Light pink
    'Planning': { bg: '#22c55e', text: '#fff' },  // Green
    'Health': { bg: '#3b82f6', text: '#fff' },  // Blue
    'Child Protection': { bg: '#ef4444', text: '#fff' },  // Red
    'WASH': { bg: '#eab308', text: '#000' },  // Yellow
    'Nutrition': { bg: '#facc15', text: '#000' },  // Yellow variant
    'Communication': { bg: '#a855f7', text: '#fff' },  // Purple
    'Social Policy': { bg: '#c084fc', text: '#fff' },  // Light purple
    'Operations': { bg: '#8b5cf6', text: '#fff' },  // Violet
    'PFP': { bg: '#14b8a6', text: '#fff' },  // Teal
    'Education': { bg: '#06b6d4', text: '#fff' },  // Cyan
    'default': { bg: '#6b7280', text: '#fff' }  // Gray
};

// Island configuration - groups of adjacent desk zones
const ISLANDS = [
    { name: 'East 1-2', zones: ['East 1', 'East 2'] },
    { name: 'East 3-5', zones: ['East 3', 'East 4', 'East 5'] },
    { name: 'East 6-8', zones: ['East 6', 'East 7', 'East 8'] },
    { name: 'West 1-2', zones: ['West 1', 'West 2'] },
    { name: 'West 3-5', zones: ['West 3', 'West 4', 'West 5'] },
    { name: 'West 6-7', zones: ['West 6', 'West 7'] },
];

function DeskMap({
    desks = [],
    selectedDate,
    bookingsForDate = [],
    onDeskClick,
    userEmail,
    userUnit = '', // User's assigned unit - can only book desks in this unit
    selectedDeskId,
    monitorPreference = 'Single Monitor',
    funMode = false, // Controlled prop
}) {
    // Group desks by zone
    const groupedDesks = useMemo(() => {
        const grouped = {};
        desks.forEach((desk) => {
            const key = desk.zone || 'Unknown';
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(desk);
        });
        return grouped;
    }, [desks]);

    // Get unit color for a desk
    const getUnitColor = (unit) => {
        return UNIT_COLORS[unit] || UNIT_COLORS.default;
    };

    const [deskStates, setDeskStates] = useState({});
    const [selectedDesk, setSelectedDesk] = useState(null);
    const [hoveredBookedDesk, setHoveredBookedDesk] = useState(null);
    const [delayedFunMode, setDelayedFunMode] = useState(false);

    // Manage Fun Mode Delay (3 seconds)
    useEffect(() => {
        if (funMode) {
            setDelayedFunMode(false); // Ensure it starts off
            const timer = setTimeout(() => {
                setDelayedFunMode(true);
            }, 3000);
            return () => clearTimeout(timer);
        } else {
            setDelayedFunMode(false);
        }
    }, [funMode, bookingsForDate]); // Restart timer if data refreshed

    // Sync Global Physics Engine with Delayed State
    useEffect(() => {
        // Sync state
        setFunMode(delayedFunMode);

        // If active, force update content
        if (delayedFunMode) {
            setTimeout(() => updateFunMode(), 50);
        }

        return () => setFunMode(false);
    }, [delayedFunMode, desks]);

    useEffect(() => {
        const states = {};
        desks.forEach((desk) => {
            const booking = bookingsForDate.find((b) => b.desk_id === desk.id);

            // Check if desk is reserved or in maintenance
            if (desk.status === 'maintenance') {
                states[desk.id] = 'maintenance';
            } else if (desk.status === 'reserved') {
                // Check if user is authorized for this reserved desk
                let isAuthorized = desk.reserved_for === userEmail;

                // Also check reserved_users array
                if (!isAuthorized && desk.reserved_users) {
                    try {
                        const authorizedUsers = typeof desk.reserved_users === 'string'
                            ? JSON.parse(desk.reserved_users)
                            : desk.reserved_users;
                        isAuthorized = Array.isArray(authorizedUsers) && authorizedUsers.includes(userEmail);
                    } catch {
                        // Fallback to CSV parsing
                        if (typeof desk.reserved_users === 'string') {
                            const userList = desk.reserved_users.split(',').map(u => u.trim());
                            isAuthorized = userList.includes(userEmail);
                        }
                    }
                }

                if (isAuthorized) {
                    // Reserved for current user - treat as available
                    if (booking) {
                        states[desk.id] = booking.user_email === userEmail ? 'user-booking' : 'other-booking';
                    } else {
                        states[desk.id] = 'reserved-for-me';
                    }
                } else {
                    // Reserved for someone else
                    states[desk.id] = 'reserved-other';
                }
            } else if (booking) {
                states[desk.id] = booking.user_email === userEmail ? 'user-booking' : 'other-booking';
            } else {
                states[desk.id] = 'available';
            }
        });
        setDeskStates(states);
    }, [desks, bookingsForDate, selectedDate, userEmail]);

    useEffect(() => {
        if (selectedDeskId) {
            setSelectedDesk(selectedDeskId);
        } else {
            setSelectedDesk(null);
        }
    }, [selectedDeskId, selectedDate]);

    const handleDeskClick = (desk) => {
        const status = deskStates[desk.id] || 'available';

        // Check if desk is in user's unit (if userUnit is set)
        const isOtherUnit = userUnit && desk.unit !== userUnit;

        if (status === 'other-booking' || status === 'reserved-other' || status === 'maintenance' || isOtherUnit) {
            return;
        }
        setSelectedDesk(desk.id);
        if (typeof onDeskClick === 'function') {
            onDeskClick(desk);
        }
    };

    const normalizedPreference = (monitorPreference || 'Any').toLowerCase();
    const isAnyPreference = normalizedPreference === 'any';
    const preferredKeyword = normalizedPreference.includes('dual') ? 'dual' : 'single';

    const renderDeskGroup = (zone, unit, deskList) => {
        // Use zone as the group label (e.g., "E1", "W1")
        const zoneLabel = (zone || 'Zone').toUpperCase();

        const seats = deskList
            .slice(0, 6)
            .map((desk, index) => {
                const row = Math.floor(index / 2);
                const col = index % 2;
                const x = TABLE_LAYOUT.marginX + col * CELL_LAYOUT.width;
                const y = TABLE_LAYOUT.marginY + row * CELL_LAYOUT.height;
                return { desk, x, y };
            });

        if (seats.length === 0) {
            return null;
        }

        const renderChair = (rowIndex, side) => {
            // Find the desk at this specific position
            const col = side === 'left' ? 0 : 1;
            const deskIndex = rowIndex * 2 + col;
            const seat = seats[deskIndex];

            // Only show glow if this specific desk matches the preference
            const hasPreferredDesk = seat && (() => {
                const monitorSetup = (seat.desk.monitor_setup || 'Single Monitor').toLowerCase();
                return !isAnyPreference && monitorSetup.includes(preferredKeyword);
            })();

            const chairY = TABLE_LAYOUT.marginY + rowIndex * CELL_LAYOUT.height + CELL_LAYOUT.height / 2 - CHAIR_LAYOUT.height / 2;
            const chairX =
                side === 'left'
                    ? TABLE_LAYOUT.marginX - CHAIR_LAYOUT.offset - CHAIR_LAYOUT.width
                    : TABLE_LAYOUT.marginX + TABLE_LAYOUT.width + CHAIR_LAYOUT.offset;

            // derive chair status from the desk at this chair position
            const chairStatus = seat ? (deskStates[seat.desk.id] || 'available') : 'empty';
            const chairIsOtherUnit = seat && userUnit && seat.desk.unit !== userUnit;
            const chairClasses = ['chair', side, chairStatus];
            if (hasPreferredDesk) {
                chairClasses.push('preferred-match');
            }
            if (chairIsOtherUnit) {
                chairClasses.push('other-unit');
            }

            // Get booking info for avatar display
            const bookingDetails = seat ? bookingsForDate.find((b) => b.desk_id === seat.desk.id) : null;
            const isOtherBooking = chairStatus === 'other-booking' && bookingDetails;
            const isUserBooking = chairStatus === 'user-booking' && bookingDetails;
            const hasBooking = (isOtherBooking || isUserBooking) && bookingDetails;

            // Avatar positioning - overlay on the chair
            const avatarSize = 69; // 25% bigger than 55
            const avatarX = CHAIR_LAYOUT.width / 2; // Center on chair (relative to chair transform)
            const avatarY = CHAIR_LAYOUT.height / 2 + 5; // Center on chair, lowered
            const avatarClipId = seat ? `avatar-clip-${seat.desk.id}` : `avatar-clip-empty-${rowIndex}-${side}`;

            // Build avatar URL - Use server API which handles file resolution (DB paths, defaults, BMPs)
            const avatarUrl = bookingDetails?.user_email
                ? `/api/device-avatar/${bookingDetails.user_email}`
                : null;

            return (
                <g key={`${side}-${rowIndex}`} className={chairClasses.join(' ')} transform={`translate(${chairX}, ${chairY})`}>
                    {/* Chair elements first (bottom layer) */}
                    <rect className="chair-body" width={CHAIR_LAYOUT.width} height={CHAIR_LAYOUT.height} rx="10" ry="10" />
                    <rect className="chair-back" x={CHAIR_LAYOUT.width * 0.2} y={-8} width={CHAIR_LAYOUT.width * 0.6} height={8} rx="4" />
                    <rect className="chair-arm" x={-6} y={CHAIR_LAYOUT.height * 0.25} width={6} height={CHAIR_LAYOUT.height * 0.5} rx="3" />
                    <rect className="chair-arm" x={CHAIR_LAYOUT.width} y={CHAIR_LAYOUT.height * 0.25} width={6} height={CHAIR_LAYOUT.height * 0.5} rx="3" />
                    <circle className="chair-wheel" cx={CHAIR_LAYOUT.width * 0.15} cy={CHAIR_LAYOUT.height + 4} r={4.5} />
                    <circle className="chair-wheel" cx={CHAIR_LAYOUT.width * 0.85} cy={CHAIR_LAYOUT.height + 4} r={4.5} />

                    {/* Avatar ON TOP of chair (rendered for ALL bookings, including user's own) */}
                    {hasBooking && bookingDetails.show_avatar !== 0 && (
                        <g className="booking-avatar" transform={`translate(${avatarX}, ${avatarY})`}>
                            <defs>
                                <clipPath id={avatarClipId}>
                                    <circle cx="0" cy="0" r={avatarSize / 2} />
                                </clipPath>
                            </defs>
                            {/* Avatar circle background */}
                            <circle cx="0" cy="0" r={avatarSize / 2 + 3} className="avatar-border" />
                            {/* Avatar image OR initials fallback */}
                            {/* Avatar Fallback (Initials) - Always present behind image */}
                            <g className="avatar-fallback">
                                <circle cx="0" cy="0" r={avatarSize / 2} className="avatar-placeholder" />
                                <text x="0" y="5" className="avatar-initials">
                                    {(bookingDetails.user_name || bookingDetails.user_email || '?').charAt(0).toUpperCase()}
                                </text>
                            </g>

                            {/* Avatar Image - Overlays fallback if loads successfully */}
                            {avatarUrl && (
                                <image
                                    href={avatarUrl}
                                    x={-avatarSize / 2}
                                    y={-avatarSize / 2}
                                    width={avatarSize}
                                    height={avatarSize}
                                    clipPath={`url(#${avatarClipId})`}
                                    preserveAspectRatio="xMidYMid slice"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            )}
                            {/* Name and time labels - ABOVE avatar */}
                            <text x="0" y={-avatarSize / 2 - 20} className="avatar-name">
                                {bookingDetails.user_name || bookingDetails.user_email?.split('@')[0] || 'Booked'}
                            </text>
                            <text x="0" y={-avatarSize / 2 - 8} className="avatar-time">
                                {bookingDetails.start_time && bookingDetails.end_time
                                    ? `${bookingDetails.start_time} - ${bookingDetails.end_time}`
                                    : 'Full day'}
                            </text>
                        </g>
                    )}
                </g>
            );
        };

        return (
            <div key={zone} className={`desk-group${delayedFunMode ? ' fun-mode-active' : ''}`} aria-label={`Zone ${zoneLabel}`}>
                <svg viewBox="-80 -20 550 500" className="desk-group-svg" role="img">
                    <desc>{`Desk layout for ${zoneLabel}`}</desc>

                    <g className="group-banner">
                        <rect x="95" y="8" width="190" height="38" rx="18" className="banner-bg" />
                        <text x="190" y="32" className="banner-text">{zoneLabel}</text>
                    </g>

                    <rect
                        x={TABLE_LAYOUT.marginX}
                        y={TABLE_LAYOUT.marginY}
                        width={TABLE_LAYOUT.width}
                        height={TABLE_LAYOUT.height}
                        rx="22"
                        className="table-surface"
                    />

                    <rect
                        x={TABLE_LAYOUT.marginX - 6}
                        y={TABLE_LAYOUT.marginY - 6}
                        width={TABLE_LAYOUT.width + 12}
                        height={TABLE_LAYOUT.height + 12}
                        rx="26"
                        className="table-outline"
                    />

                    <line
                        x1={TABLE_LAYOUT.marginX + CELL_LAYOUT.width}
                        y1={TABLE_LAYOUT.marginY}
                        x2={TABLE_LAYOUT.marginX + CELL_LAYOUT.width}
                        y2={TABLE_LAYOUT.marginY + TABLE_LAYOUT.height}
                        className="table-divider"
                    />
                    <line
                        x1={TABLE_LAYOUT.marginX}
                        y1={TABLE_LAYOUT.marginY + CELL_LAYOUT.height}
                        x2={TABLE_LAYOUT.marginX + TABLE_LAYOUT.width}
                        y2={TABLE_LAYOUT.marginY + CELL_LAYOUT.height}
                        className="table-divider"
                    />
                    <line
                        x1={TABLE_LAYOUT.marginX}
                        y1={TABLE_LAYOUT.marginY + CELL_LAYOUT.height * 2}
                        x2={TABLE_LAYOUT.marginX + TABLE_LAYOUT.width}
                        y2={TABLE_LAYOUT.marginY + CELL_LAYOUT.height * 2}
                        className="table-divider"
                    />

                    {[0, 1, 2].map((row) => renderChair(row, 'left'))}
                    {[0, 1, 2].map((row) => renderChair(row, 'right'))}

                    {seats.map(({ desk, x, y }) => {
                        const status = deskStates[desk.id] || 'available';
                        const isSelected = selectedDesk === desk.id;
                        const isOtherUnit = userUnit && desk.unit !== userUnit;
                        const isDisabled = status === 'other-booking' || status === 'reserved-other' || status === 'maintenance' || isOtherUnit;
                        const baseClasses = ['desk-seat', status];
                        if (isSelected) baseClasses.push('selected');
                        if (isOtherUnit) baseClasses.push('other-unit');
                        const seatClass = baseClasses.join(' ');
                        const groupClass = `desk-seat-group ${status}${isDisabled ? ' disabled' : ''}${isOtherUnit ? ' other-unit' : ''}`;

                        const bookingDetails = bookingsForDate.find((b) => b.desk_id === desk.id);

                        // Get unit color for this desk
                        const unitColor = getUnitColor(desk.unit);

                        // Status label - show unit abbreviation for other units, or booking status
                        const statusLabel = (() => {
                            if (status === 'maintenance') return 'Maintenance';
                            if (status === 'reserved-other') return 'Reserved';
                            if (status === 'reserved-for-me') return 'Reserved';
                            if (status === 'user-booking') return 'Your Booking';
                            if (status === 'other-booking') return 'Booked';
                            // For available desks, show unit name
                            return desk.unit || 'Free';
                        })();

                        let tooltip = `${desk.id} • ${desk.monitor_setup || 'Single Monitor'} • ${desk.unit || 'Unassigned'}`;
                        if (bookingDetails) {
                            const fullName = bookingDetails.user_name || bookingDetails.user_email;
                            const timeRange = bookingDetails.start_time && bookingDetails.end_time
                                ? `${bookingDetails.start_time} - ${bookingDetails.end_time}`
                                : 'Full day';
                            tooltip = `${desk.id} • ${bookingDetails.booking_date} • ${fullName} • ${timeRange}`;
                        }

                        // Keep desk background white, only color the unit name text
                        const deskStyle = {}; // White background from CSS
                        const textStyle = {}; // Default text color
                        const unitTextStyle = { fill: unitColor.bg, fontWeight: '700', fontSize: desk.unit && desk.unit.length > 10 ? '0.65rem' : '0.85rem' }; // Reduce font for long names like Child Protection

                        return (
                            <g
                                key={desk.id}
                                className={groupClass}
                                onClick={isDisabled ? undefined : () => handleDeskClick(desk)}
                                role="button"
                                tabIndex={isDisabled ? -1 : 0}
                                aria-disabled={isDisabled}
                                aria-pressed={isSelected}
                                aria-label={`${desk.id} ${statusLabel}`}
                                onKeyDown={(event) => {
                                    if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                                        event.preventDefault();
                                        handleDeskClick(desk);
                                    }
                                }}
                            >
                                <rect
                                    x={x + 4}
                                    y={y + 4}
                                    width={CELL_LAYOUT.width - 8}
                                    height={CELL_LAYOUT.height - 8}
                                    rx="14"
                                    className={seatClass}
                                    style={deskStyle}
                                />
                                <text
                                    x={x + CELL_LAYOUT.width / 2}
                                    y={y + CELL_LAYOUT.height / 2 - 12}
                                    className="desk-seat-label"
                                    style={textStyle}
                                >
                                    {desk.id}
                                </text>
                                {desk.adjustable_monitor === 1 ? (
                                    <text
                                        x={x + CELL_LAYOUT.width / 2}
                                        y={y + CELL_LAYOUT.height / 2 + 5}
                                        className="desk-seat-monitor"
                                        style={{ fill: '#dc2626', fontWeight: 'bold', fontSize: '0.6rem' }}
                                    >
                                        <tspan x={x + CELL_LAYOUT.width / 2} dy="0">Adjustable Monitor</tspan>
                                        <tspan x={x + CELL_LAYOUT.width / 2} dy="12">Priority Desk</tspan>
                                    </text>
                                ) : (
                                    <text
                                        x={x + CELL_LAYOUT.width / 2}
                                        y={y + CELL_LAYOUT.height / 2 + 6}
                                        className="desk-seat-monitor"
                                        style={textStyle}
                                    >
                                        {desk.monitor_setup || 'Single Monitor'}
                                    </text>
                                )}

                                {/* status text - show unit name for available desks */}
                                <text
                                    x={x + CELL_LAYOUT.width / 2}
                                    y={y + CELL_LAYOUT.height / 2 + 28}
                                    className="desk-seat-status"
                                    style={status === 'available' ? unitTextStyle : textStyle}
                                >
                                    {statusLabel}
                                </text>

                                {(status === 'other-booking' || status === 'reserved-other' || status === 'maintenance') && (
                                    <g
                                        className="desk-info-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setHoveredBookedDesk(hoveredBookedDesk === desk.id ? null : desk.id);
                                        }}
                                        onMouseEnter={() => { }}
                                        onMouseLeave={() => { }}
                                        role="button"
                                        tabIndex="0"
                                        aria-label="Show booking details"
                                    >
                                        <circle
                                            cx={x + CELL_LAYOUT.width - 12}
                                            cy={y + 12}
                                            r="8"
                                            className="info-button-circle"
                                        />
                                        <text
                                            x={x + CELL_LAYOUT.width - 12}
                                            y={y + 15}
                                            className="info-button-text"
                                        >
                                            i
                                        </text>
                                    </g>
                                )}

                                <title>{tooltip}</title>
                            </g>
                        );
                    })}
                </svg>
            </div>
        );
    };

    return (
        <div className="desk-map-wrapper">
            {desks.length === 0 && (
                <div className="map-instructions warning">
                    <p>⚠️ No desks available for your unit. Please contact your administrator.</p>
                </div>
            )}

            <div className="desk-map-container">
                {Object.keys(groupedDesks).length > 0 ? (
                    <div className="desk-groups">
                        {Object.keys(groupedDesks).sort().map(zone => {
                            const deskList = groupedDesks[zone];

                            // Only show zones that have desks from user's unit
                            if (userUnit) {
                                const hasUserUnitDesks = deskList.some(desk => desk.unit === userUnit);
                                if (!hasUserUnitDesks) return null;
                            }

                            return renderDeskGroup(zone, null, deskList);
                        })}
                    </div>
                ) : (
                    <div className="no-desks">
                        <p>No desks available for your unit.</p>
                    </div>
                )}
            </div>

            {hoveredBookedDesk && (() => {
                const bookedDesk = desks.find(d => d.id === hoveredBookedDesk);
                const bookingDetails = bookingsForDate.find((b) => b.desk_id === hoveredBookedDesk);
                const deskStatus = deskStates[hoveredBookedDesk];

                if (bookedDesk) {
                    if (deskStatus === 'reserved-other') {
                        // Show reserved desk info
                        return (
                            <div className="desk-info-popup">
                                <div className="popup-content">
                                    <div className="popup-close" onClick={() => setHoveredBookedDesk(null)}>×</div>
                                    <h4>{bookedDesk.id} - Reserved by Admin</h4>
                                    <div className="popup-info">
                                        <p><strong>Status:</strong> Reserved</p>
                                        <p><strong>Monitor:</strong> {bookedDesk.monitor_setup || 'Single Monitor'}</p>
                                        <p style={{ color: '#c92a2a', fontWeight: 'bold' }}>This desk has been reserved by the administrator for another user.</p>
                                    </div>
                                </div>
                            </div>
                        );
                    } else if (deskStatus === 'maintenance') {
                        // Show maintenance info
                        return (
                            <div className="desk-info-popup">
                                <div className="popup-content">
                                    <div className="popup-close" onClick={() => setHoveredBookedDesk(null)}>×</div>
                                    <h4>{bookedDesk.id} - Under Maintenance</h4>
                                    <div className="popup-info">
                                        <p><strong>Status:</strong> Maintenance</p>
                                        <p><strong>Monitor:</strong> {bookedDesk.monitor_setup || 'Single Monitor'}</p>
                                        <p style={{ color: '#495057', fontWeight: 'bold' }}>This desk is currently under maintenance and cannot be booked.</p>
                                    </div>
                                </div>
                            </div>
                        );
                    } else if (bookingDetails) {
                        // Show booking info
                        const fullName = bookingDetails.user_name || bookingDetails.user_email;
                        const timeRange = bookingDetails.start_time && bookingDetails.end_time
                            ? `${bookingDetails.start_time} - ${bookingDetails.end_time}`
                            : 'Full day';
                        return (
                            <div className="desk-info-popup">
                                <div className="popup-content">
                                    <div className="popup-close" onClick={() => setHoveredBookedDesk(null)}>×</div>
                                    <h4>{bookedDesk.id} - Booking Details</h4>
                                    <div className="popup-info">
                                        <p><strong>Booked by:</strong> {fullName}</p>
                                        <p><strong>Date:</strong> {bookingDetails.booking_date}</p>
                                        <p><strong>Time:</strong> {timeRange}</p>
                                        <p><strong>Monitor:</strong> {bookedDesk.monitor_setup || 'Single Monitor'}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                }
                return null;
            })()}

            <div className="legend">
                <div className="legend-header">
                    <h4>Legend:</h4>
                    <button className="btn-monitor-match" title="Desks matching your monitor preference">
                        ✓ Match Monitor Preference
                    </button>
                </div>
                <div className="legend-content">
                    <p className="legend-description">
                        <strong>Note:</strong> Desk tiles now only show the desk ID. <strong>Chairs</strong> indicate booking status — <span style={{ color: '#1e9a4b', fontWeight: 700 }}>Green</span> = Available, <span style={{ color: '#0b78d1', fontWeight: 700 }}>Blue</span> = your booking, <span style={{ color: '#8c959f', fontWeight: 700 }}>Gray</span> = Booked/Reserved by others, <span style={{ color: '#ffd966', fontWeight: 700 }}>Gold</span> = Reserved for you, <span style={{ color: '#ffb6b6', fontWeight: 700 }}>Red</span> = Reserved/Blocked.
                    </p>
                    <div className="legend-items">
                        <div className="legend-item">
                            <div className="legend-desk available"></div>
                            <span>Available - Click to book</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-desk user-booking"></div>
                            <span>your booking</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-desk reserved-for-me"></div>
                            <span>Reserved for You</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-desk other-booking"></div>
                            <span>Reserved by Others</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-desk reserved-other"></div>
                            <span>Admin Reserved</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-desk maintenance"></div>
                            <span>Under Maintenance</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default DeskMap;
